import * as yup from 'yup';
import { ArrowDown, ArrowUp, Info, Plus, Trash2 } from 'react-feather';
import { ChangeEvent, useRef, useState } from 'react';
import { FormManager, useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { PracticalInfoTopic } from '__generated__/graphql';

import { ErrorBag, getServerErrors } from '../../../../common/helpers';
import { Event } from './helpers';
import { PracticalInfoIcon, getPracticalInfoFactTitle } from '../../../../common/practicalInfo';
import { TranslatableEditor, TranslatableInput } from '../../../Translations/Inputs';
import { Translation, translationsForAttribute } from '../../../Translations/helpers';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useGeneratePracticalInfoModal from './useGeneratePracticalInfoModal';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

export interface EditEventPracticalInfoFormProps {
  event: Event;
  topics: PracticalInfoTopic[];
}

const editEventPracticalInfoSchema = yup.object({
  practical_info_facts: yup.array(yup.object({
    title: yup.string().required(),
    description: yup.string().required(),
  })),
});

interface PracticalInformationFactValue {
  id: string | null;
  title: string;
  title_translations: Translation[];
  description: string;
  description_translations: Translation[];
}

const emptyFact = (overrides: Partial<PracticalInformationFactValue> = {}): PracticalInformationFactValue => ({
  id: null,
  title: '',
  title_translations: [],
  description: '',
  description_translations: [],
  ...overrides,
});

const getValues = (event: Event) => ({
  practical_info_facts: event.practical_info_facts.map((fact) => ({
    id: fact.id,
    title: fact.title,
    title_translations: translationsForAttribute(fact.translations, 'title'),
    description: fact.description || '',
    description_translations: translationsForAttribute(fact.translations, 'description'),
  })),
});

const EditEventPracticalInfoForm = ({ event, topics }: EditEventPracticalInfoFormProps) => {
  const { t } = useTranslation();
  const form = useForm({ values: getValues(event) });
  const [formKey, setFormKey] = useState(0);
  const inputKeyCountRef = useRef(form.values.practical_info_facts.length);
  const inputKeysRef = useRef(form.values.practical_info_facts.map((_, index) => index));

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      practical_info: {
        practical_info_facts: form.values.practical_info_facts
          .filter((value) => value.title?.trim() && value.description?.trim()),
      },
    },
    onSuccess: (updatedEvent) => {
      form.reset(getValues(updatedEvent));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventPracticalInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'practical_info'),
  });

  const handleAdd = () => {
    inputKeyCountRef.current += 1;
    inputKeysRef.current = [...inputKeysRef.current, inputKeyCountRef.current];
    form.set((values) => ({
      ...values,
      practical_info_facts: [...values.practical_info_facts, emptyFact()],
    }));
  };

  const handleDelete = (index: number) => {
    inputKeysRef.current = inputKeysRef.current.filter((_, itemIndex) => itemIndex !== index);
    form.set((values) => ({
      ...values,
      practical_info_facts: values.practical_info_facts.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= form.values.practical_info_facts.length) {
      return;
    }

    const inputKeys = [...inputKeysRef.current];
    [inputKeys[index], inputKeys[targetIndex]] = [inputKeys[targetIndex], inputKeys[index]];
    inputKeysRef.current = inputKeys;

    form.set((values) => {
      const facts = [...values.practical_info_facts];
      [facts[index], facts[targetIndex]] = [facts[targetIndex], facts[index]];

      return { ...values, practical_info_facts: facts };
    });
  };

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('practical_info')}
          </UI.Legend>
          <UI.Div>
            {t('practical_info_description')}
          </UI.Div>
        </UI.Div>

        {form.values.practical_info_facts.map((fact, index) => (
          <PracticalInformationFactForm
            form={form}
            errors={errors}
            index={index}
            event={event}
            topics={topics}
            canMoveUp={index > 0}
            canMoveDown={index < form.values.practical_info_facts.length - 1}
            key={inputKeysRef.current[index]}
            onDelete={() => handleDelete(index)}
            onMoveUp={() => handleMove(index, 'up')}
            onMoveDown={() => handleMove(index, 'down')}
          />
        ))}
        <UI.Div>
          <UI.Button onClick={handleAdd}>
            <UI.Icon><Plus /></UI.Icon>
            {' '}
            {t('add_topic')}
          </UI.Button>
        </UI.Div>

        <UI.HR />

        <UI.FormButtons form={form} submitLabel={t('save_event')} loading={loading} valid={valid} />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface PracticalInformationFactFormProps {
  form: FormManager;
  errors: ErrorBag;
  index: number;
  event: Event;
  topics: PracticalInfoTopic[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const PracticalInformationFactForm = ({
  form, errors, index, event, topics, canMoveUp, canMoveDown, onDelete, onMoveUp, onMoveDown,
}: PracticalInformationFactFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const key = (name?: string) => `practical_info_facts.${index}${name ? `.${name}` : ''}`;

  const currentTitle = form.values.practical_info_facts[index].title;
  const currentValue = form.values.practical_info_facts[index].description;
  const matchedTopic = topics.find((topicOption) => topicOption === currentTitle);
  const [topic, setTopic] = useState(() => matchedTopic ?? (currentTitle ? 'other' : ''));
  const [editorKey, setEditorKey] = useState(0);

  const handleTopicChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const { value } = changeEvent.target;
    setTopic(value);
    form.set(key('title'), value && value !== 'other' ? value : '');
    form.setTouched(key('title'), false);
  };

  const factLabel = getPracticalInfoFactTitle(currentTitle, topics, t) || formatNumber(index + 1);

  const [openGenerateModal] = useGeneratePracticalInfoModal({
    event,
    topic: currentTitle,
    topicLabel: getPracticalInfoFactTitle(currentTitle, topics, t),
    currentValue,
    defaultContext: topic ? t(`practical_info_placeholders.${topic}`) : '',
    onGenerated: (description) => {
      form.set(key('description'), description);
      form.set(key('description_translations'), []);
      setEditorKey((value) => value + 1);
    },
  });

  const canGenerate = !!event.website_url;

  return (
    <UI.Card sc={{ outline: 'gray.200', hasPanels: true, borderRadius: 'lg' }}>
      <UI.FlexContainer
        sc={{ justifyContent: 'space-between', alignItems: 'center', background: 'gray.50', py: 3 }}
      >
        <UI.FlexContainer
          sc={{ justifyContent: 'space-between', alignItems: 'center', gap: 0.25 }}
        >
          <UI.Legend sc={{ fontWeight: 500 }}>
            <UI.Icon>
              <Info />
            </UI.Icon>
            {' '}
            {t('topic_number', { number: formatNumber(index + 1) })}
            {' '}
          </UI.Legend>
        </UI.FlexContainer>
        <UI.FlexContainer sc={{ alignItems: 'center', my: -0.75 }}>
          <UI.Button
            sc={{ blank: true, size: 'sm', color: 'gray.500' }}
            onClick={onMoveUp}
            disabled={!canMoveUp}
            title={t('move_up_option', { option: factLabel })}
          >
            <UI.Icon><ArrowUp /></UI.Icon>
          </UI.Button>
          <UI.Button
            onClick={onMoveDown}
            disabled={!canMoveDown}
            sc={{ blank: true, color: 'gray.500', size: 'sm' }}
            aria-label={t('move_down_option', { option: factLabel })}
          >
            <UI.Icon>
              <ArrowDown />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            sc={{ blank: true, size: 'sm', color: 'gray.500' }}
            onClick={onDelete}
            title={t('delete_option', { option: factLabel })}
          >
            <UI.Icon><Trash2 /></UI.Icon>
          </UI.Button>
        </UI.FlexContainer>
      </UI.FlexContainer>
      <UI.FormGrid>
        <UI.InputGroup sc={{ valid: !errors[key('title')], touched: !!form.getTouched(key('title')) }}>
          <UI.InputLabel htmlFor={`event_practical_info_${index}`}>
            {t('topic')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.FormGrid sc={{ columns: '1fr 1fr', gutter: 0.5 }}>
            <UI.InputWrapper>
              <UI.Icon>
                <PracticalInfoIcon topic={topic} />
              </UI.Icon>
              <UI.Select
                id={`event_practical_info_${index}`}
                aria-label={`${t('practical_info')} ${formatNumber(index + 1)}`}
                value={topic}
                sc={{ pl: 5 }}
                onChange={handleTopicChange}
              >
                {!topic && <option value=""> </option>}
                {topics.map((topicOption) => (
                  <option value={topicOption} key={topicOption}>{t(`common:practical_info.${topicOption}`)}</option>
                ))}
                <option value="other">{t('other_placeholder')}</option>
              </UI.Select>
            </UI.InputWrapper>
            {topic === 'other' && (
              <TranslatableInput
                form={form}
                errors={errors}
                name={key('title')}
                defaultLocale={event.locale}
                title={t('title')}
                id={`event_practical_info_${index}_other`}
                aria-label={`${t('practical_info')} ${formatNumber(index + 1)}`}
                maxLength={30}
                placeholder={t('enter_topic_placeholder')}
                autoFocus={!form.get(key('title'))}
              />
            )}
          </UI.FormGrid>
          <UI.ErrorMessages attribute={t('topic')} errors={errors[key('title')]} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors[key('description')], touched: !!form.getTouched(key('description')) }}>
          <UI.FlexContainer sc={{ alignItems: 'center', mb: 1 }}>
            <UI.InputLabel id={`ContentLabel_${index}`}>
              {t('content')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            {canGenerate && (
              <UI.Button
                sc={{ blank: true, size: 'xs', ml: 1, my: -0.5 }}
                onClick={() => openGenerateModal()}
                disabled={!currentTitle.trim()}
              >
                <UI.Icon><UI.Icons.Ai /></UI.Icon>
                {' '}
                {t('generate')}
              </UI.Button>
            )}
          </UI.FlexContainer>
          <TranslatableEditor
            form={form}
            errors={errors}
            name={key('description')}
            defaultLocale={event.locale}
            title={`${t('practical_info')} ${index + 1}`}
            options={{
              headings: [3],
              placeholder: topic ? t(`practical_info_placeholders.${topic}`) : null,
            }}
            key={`${topic}-${editorKey}`}
            attributes={{
              'aria-labelledby': `ContentLabel_${index}`,
            }}
          />
          <UI.ErrorMessages attribute={t('description')} errors={errors[key('description')]} />
        </UI.InputGroup>
      </UI.FormGrid>
    </UI.Card>
  );
};

export default EditEventPracticalInfoForm;
